import { h } from '@atomify/jsx';
import { MapDataItem, MapTheme } from '@bpd-library/types';
import { calculateCenter, throttle } from '@bpd-library/utilities';
import { GeoJSONSource, LngLatLike, Marker } from 'mapbox-gl';

import { clearHover, updateClick, updateHover } from '../../../store';
import { MAP_FLY_DURATION, MAP_FLY_EASING_FUNCTION } from '../constants';
import { FeatureProperties, parseFeatureProperties } from '../parse-feature-properties';

type MapMarkers = { [key: string]: mapboxgl.Marker };

type MarkerObject = {
    cache: MapMarkers;
    onScreen: MapMarkers;
};

interface ClusterChildren {
    cluster: Marker;
    clusterSource: GeoJSONSource;
    clusterId: number;
}

interface MarkersToCluster {
    cluster: Marker;
    features: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
}

const MAP_HOVER_CLASS = 'map--hovering';
const POINTER_FADEOUT_DURATION = 300;

const setupPointers = (map: mapboxgl.Map, theme: MapTheme, element: HTMLElement) => {
    const { pointer: pointerTheme } = theme;
    const clusters: MarkerObject = { cache: {}, onScreen: {} };
    const pointers: MarkerObject = { cache: {}, onScreen: {} };
    const sourceNames: string[] = [];

    init();

    function init() {
        theme.pointer?.settings.forEach(({ variant, minzoom, maxzoom }) => {
            sourceNames.push(variant);

            map.addSource(variant, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
                cluster: true,
                clusterRadius: 50,
                clusterMaxZoom: 8,
                promoteId: 'id',
            });

            const layerSettings: mapboxgl.AnyLayer | mapboxgl.CustomLayerInterface = {
                id: variant,
                type: 'circle',
                source: variant,
                filter: ['!=', 'cluster', true],
                paint: {
                    'circle-opacity': 0,
                },
            };

            if (minzoom) layerSettings.minzoom = minzoom;
            if (maxzoom) layerSettings.maxzoom = maxzoom;

            map.addLayer(layerSettings);
        });

        bindEvents();
    }

    function bindEvents() {
        map.on('render', throttle(updateMarkers, 200));
    }

    function setClusterChildrenData({ cluster, clusterSource, clusterId }: ClusterChildren) {
        clusterSource?.getClusterChildren(clusterId!, (_, features) => {
            const markersLength = features[0].properties?.point_count;
            const OFFSET = 0;

            clusterSource.getClusterLeaves(clusterId, markersLength, OFFSET, (_, features) => {
                const config: MarkersToCluster = {
                    cluster,
                    features,
                };
                addMarkersToCluster(config);
            });
        });
    }

    function addMarkersToCluster({ cluster, features }: MarkersToCluster) {
        let clusterChildren: string[];

        clusterChildren = features.map((feature: any) => {
            return feature.properties?.id;
        });

        clusterChildren = clusterChildren?.filter(Boolean);
        cluster!.getElement().dataset.children = String(clusterChildren);
    }

    function addCluster(
        clus: GeoJSON.Feature,
        variant: string,
    ): [mapboxgl.Marker | undefined, number | undefined] {
        const { properties, geometry } = clus;
        const { coordinates } = geometry as GeoJSON.Point;
        const { cluster_id: id, point_count: count } = properties as {
            cluster_id?: number;
            point_count?: number;
        };

        let cluster: mapboxgl.Marker | undefined;

        if (id && count) {
            cluster = clusters.cache[id];
            if (!cluster) {
                cluster = new Marker({
                    element: (
                        <button
                            id={id}
                            class="map__cluster"
                            onMouseenter={() => {
                                element.classList.add(MAP_HOVER_CLASS);
                            }}
                            onMouseleave={() => {
                                element.classList.remove(MAP_HOVER_CLASS);
                            }}
                            onClick={() => {
                                element.classList.remove(MAP_HOVER_CLASS);

                                (map.getSource(variant) as GeoJSONSource).getClusterExpansionZoom(
                                    id,
                                    (err, zoom) => {
                                        if (err) return;

                                        map.easeTo({
                                            center: coordinates as LngLatLike,
                                            zoom,
                                            duration: MAP_FLY_DURATION,
                                            easing: MAP_FLY_EASING_FUNCTION,
                                        });
                                    },
                                );
                            }}>
                            {pointerTheme!.cluster(count)}
                        </button>
                    ),
                }).setLngLat(coordinates as LngLatLike);

                clusters.cache[id] = cluster;
            }
            const clusterSource = map.getSource(variant) as GeoJSONSource;
            const config: ClusterChildren = {
                cluster,
                clusterSource,
                clusterId: id,
            };

            setClusterChildrenData(config);

            if (!clusters.onScreen[id]) cluster?.addTo(map);
        }

        return [cluster, id];
    }

    function addPointer({
        properties,
    }: GeoJSON.Feature): [mapboxgl.Marker | undefined, string | undefined] {
        const mapDataItem = parseFeatureProperties(properties as FeatureProperties) as MapDataItem;
        const { id, variant, coordinates } = mapDataItem;
        const setting = pointerTheme!.settings.find((setting) => setting.variant === variant);

        if (!setting) return [undefined, undefined];

        let pointer: mapboxgl.Marker | undefined = pointers.cache[id];
        const center = calculateCenter(coordinates);

        if (!pointer) {
            pointer = new Marker({
                anchor: 'center',
                element: (
                    <button
                        id={`pointer-${id}`}
                        onMouseenter={() => {
                            element.classList.add(MAP_HOVER_CLASS);
                            updateHover(mapDataItem);
                        }}
                        onMouseleave={() => {
                            element.classList.remove(MAP_HOVER_CLASS);
                            clearHover();
                        }}
                        onClick={(e: Event) => {
                            e.stopPropagation();
                            updateClick(mapDataItem);
                        }}
                        class={`map__pointer${
                            setting.disablePointerEvents ? ' map__pointer--disabled' : ''
                        }`}>
                        {setting.createElement(mapDataItem)}
                    </button>
                ),
            }).setLngLat(center);

            pointers.cache[id] = pointer;
        }

        if (!pointers.onScreen[id]) pointer.addTo(map);

        return [pointer, id];
    }

    function updateMarkers() {
        const newClusters: MapMarkers = {};
        const newPointers: MapMarkers = {};

        theme.pointer?.settings.forEach(({ variant }) => {
            const features = map.querySourceFeatures(variant);

            features.forEach((feature) => {
                const { properties } = feature;

                if (properties?.cluster) {
                    const [cluster, id] = addCluster(feature, variant);

                    if (cluster && id) newClusters[id] = cluster;
                } else {
                    const [pointer, id] = addPointer(feature);

                    if (pointer && id) newPointers[id] = pointer;
                }
            });
        });

        Object.keys(clusters.onScreen).forEach((id) => {
            if (!newClusters[id]) clusters.onScreen[id].remove();
        });

        Object.keys(pointers.onScreen).forEach((id) => {
            if (!newPointers[id]) {
                fadeoutPointer(pointers.onScreen[id]);
            }
        });

        clusters.onScreen = newClusters;
        pointers.onScreen = newPointers;
    }

    function fadeoutPointer(pointer: mapboxgl.Marker) {
        const pointerElement = pointer.getElement();

        pointerElement.classList.add('map__pointer--fadeout');

        setTimeout(() => {
            pointer.remove();

            pointerElement.classList.remove('map__pointer--fadeout');
        }, POINTER_FADEOUT_DURATION);
    }
};

export default setupPointers;
